import type { DamageEvent } from "@eldrun/protocol";
import type { Camera } from "three";
import { Vector3 } from "three";

export type DamageKind = "normal" | "critico" | "fraqueza" | "patua" | "salvo";

/** Texto e cor do número (doc 02 §19: branco, amarelo crítico, laranja fraqueza, lilás Patuá). */
export function damageLabel(e: Pick<DamageEvent, "amount" | "absorbed" | "crit" | "weak" | "saved">): { text: string; kind: DamageKind } {
  const kind: DamageKind = e.saved ? "salvo" : e.absorbed >= e.amount ? "patua" : e.crit ? "critico" : e.weak ? "fraqueza" : "normal";
  return { text: e.saved ? `${e.amount} · por um fio` : e.crit ? `${e.amount}!` : String(e.amount), kind };
}

/** Distância máxima em que os números aparecem. */
export const NUMBERS_RANGE = 60;
const LIFE = 2.5;
const FADE_FROM = 1.5;
const POOL = 24;

interface Floating {
  el: HTMLElement;
  pos: Vector3;
  vy: number;
  drift: number;
  age: number;
  active: boolean;
}

/** Números de dano que sobem do alvo e somem (DOM, projetado da posição 3D a cada quadro). */
export class DamageNumbers {
  private readonly root: HTMLElement;
  private readonly items: Floating[] = [];
  private readonly scratch = new Vector3();
  private next = 0;

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.id = "numeros";
    this.root.setAttribute("aria-hidden", "true");
    parent.appendChild(this.root);
    for (let i = 0; i < POOL; i++) {
      const el = document.createElement("span");
      el.className = "numero";
      el.style.display = "none";
      this.root.appendChild(el);
      this.items.push({ el, pos: new Vector3(), vy: 0, drift: 0, age: 0, active: false });
    }
  }

  /** `source` (F63): a cor de quem bateu e se é um aliado (número menor e mais apagado). */
  spawn(e: DamageEvent, camera: Camera, source?: { color: string; ally: boolean }): void {
    if (camera.position.distanceTo(this.scratch.set(e.x, e.y, e.z)) > NUMBERS_RANGE) return;
    const item = this.items[this.next] as Floating;
    this.next = (this.next + 1) % POOL;
    const label = damageLabel(e);
    item.el.textContent = label.text;
    item.el.className = `numero ${label.kind}${source?.ally ? " aliado" : ""}`;
    if (source) item.el.style.setProperty("--cor", source.color);
    else item.el.style.removeProperty("--cor");
    item.pos.set(e.x, e.y, e.z);
    item.vy = 1.6;
    item.drift = (Math.random() - 0.5) * 0.8;
    item.age = 0;
    item.active = true;
  }

  update(dt: number, camera: Camera, width: number, height: number): void {
    for (const item of this.items) {
      if (!item.active) continue;
      item.age += dt;
      if (item.age >= LIFE) {
        item.active = false;
        item.el.style.display = "none";
        continue;
      }
      item.vy = Math.max(0.1, item.vy - 2 * dt);
      item.pos.y += item.vy * dt;
      item.pos.x += item.drift * dt;
      const p = this.scratch.copy(item.pos).project(camera);
      if (p.z > 1 || p.z < -1) {
        item.el.style.display = "none";
        continue;
      }
      item.el.style.display = "block";
      item.el.style.transform = `translate(${((p.x + 1) / 2) * width}px, ${((1 - p.y) / 2) * height}px) translate(-50%, -50%)`;
      item.el.style.opacity = String(item.age < FADE_FROM ? 1 : 1 - (item.age - FADE_FROM) / (LIFE - FADE_FROM));
    }
  }

  remove(): void {
    this.root.remove();
  }
}
